import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useWorkspaceMutations from "common/datahooks/useWorkspaceMutations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useGTM from "common/hooks/useGTM";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { WorkspaceNameSchema } from "common/schemas";

import CreateWorkspaceImage from "assets/images/create-workspace.webp";

export default function CreateWorkspaces({ isDialog }: { isDialog: boolean }) {
  const isDesktop = useTwBreakpoint("lg");

  const { sendEvent } = useGTM();
  const navigate = useNavigate();
  const { createWorkspace, isCreatingWorkspace } = useWorkspaceMutations();
  const { workspaces } = useWorkspaces();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: valibotResolver(WorkspaceNameSchema),
  });

  function onSubmit({ name }: { name: string }) {
    if (workspaces.find((workspace) => workspace.name === name.trim())) {
      setError("name", { message: "Workspace with this name already exists" });
    } else {
      createWorkspace({ name }).then((workspace) => {
        sendEvent("workspace_created");
        navigate(`/workspaces/${workspace.id}`);
      });
    }
  }

  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const formFieldComponent = (
    <FormField
      htmlFor="workspaceName"
      label="Workspace name"
      error={errors.name?.message}
      size={isDesktop ? "md" : "lg"}
    >
      <Input
        variant={isDesktop ? "md" : "lg"}
        id="workspaceName"
        placeholder="Name your workspace"
        {...register("name")}
      />
    </FormField>
  );

  const buttonComponent = (
    <Button type="submit" isLoading={isCreatingWorkspace}>
      Create
    </Button>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isDialog ? (
        <>
          <Header
            title="Create a new workspace"
            imgSrc={CreateWorkspaceImage}
            subtitle="Create a new place to manage your campaigns and collaborate with your team"
          />
          <Body>{formFieldComponent}</Body>
          <Footer className="justify-end">
            <RenderIf condition={isDialog}>
              <Close asChild>
                <Button variant="secondary-black">Cancel</Button>
              </Close>
            </RenderIf>
            {buttonComponent}
          </Footer>
        </>
      ) : (
        <div className="flex flex-col">
          <img
            src={CreateWorkspaceImage}
            alt=""
            height={254}
            className="mx-auto"
          />
          <h1 className="text-xl-bold text-center mb-2 mt-4">
            Create your first workspace
          </h1>
          <p className="text-sm text-black-500 mb-6 text-center">
            Set up a new workspace to manage your campaigns and collaborate with
            your team
          </p>
          {formFieldComponent}
          <div className="flex justify-end mt-6">{buttonComponent}</div>
        </div>
      )}
    </form>
  );
}
